import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useMutation, useQuery } from "urql";
import { z } from "zod";

import { SearchAndSelectInput } from "@/components/FormInputs";
import { SessionLanguagePicker } from "@/components/LanguagePicker";
import { ImageUploadInput } from "@/components/inputs/ImageInput";
import { PlayerIdInput } from "@/components/inputs/PlayerIdInput";
import { Heading, Paragraph } from "@/components/typography";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FragmentType, getFragmentData, graphql } from "@/gen";
import { useAuth } from "@/hooks/useAuth";
import { useViewerContext } from "@/hooks/useViewerContext";
import {
    COUNTRY_CODES,
    CountryCode,
    LanguageCode,
    countries,
    countryCodesByNames,
    countryNamesByCodes,
    getFlagEmoji,
    supportedLanguages,
} from "@/utils/i18n";
import { useLanguageCodeWithDefault } from "@/utils/language";

const useCommentatorProfile = (languageCode: LanguageCode) => {
    const [result] = useQuery({
        query: graphql(`
            query ViewerCommentatorProfileQuery(
                $commentatorDescriptionInput: CommentatorDescriptionInput!
            ) {
                viewer {
                    id
                    commentator {
                        id
                        ...CommentatorProfileForm_Commentator
                    }
                }
            }
        `),
        variables: {
            commentatorDescriptionInput: {
                languageCode,
            },
        },
    });

    return {
        commentator: result.data?.viewer?.commentator,
        loading: result.fetching,
        error: result.error,
    };
};

export default function CommentatorProfile() {
    const languageCode = useLanguageCodeWithDefault();
    const { commentator, loading, error } = useCommentatorProfile(languageCode);

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error: {error.message}</div>;
    }

    return (
        <div>
            <Heading as="h1" variant="page">
                Commentator profile
            </Heading>
            <Paragraph>Update your commentator profile by language.</Paragraph>
            <Card className="mt-6">
                <CardHeader>
                    <SessionLanguagePicker />
                </CardHeader>
                <CardContent>
                    <CommentatorProfileForm commentatorFragment={commentator} />
                </CardContent>
            </Card>
        </div>
    );
}

const CommentatorProfileForm_Commentator = graphql(`
    fragment CommentatorProfileForm_Commentator on Commentator {
        id
        displayName
        imageId
        countryCode
        shortDescription(input: $commentatorDescriptionInput)
        playerId
    }
`);

const zCommentatorInput = z.object({
    displayName: z.string(),
    shortDescription: z.string(),
    imageId: z.string().uuid(),
    countryCode: z.enum(COUNTRY_CODES),
    playerId: z.string().uuid().nullable().default(null),
});
type CommentatorInput = z.infer<typeof zCommentatorInput>;

function CommentatorProfileForm({
    commentatorFragment,
}: {
    commentatorFragment?: FragmentType<typeof CommentatorProfileForm_Commentator> | null;
}) {
    const { authUser } = useAuth();

    const commentator = getFragmentData(CommentatorProfileForm_Commentator, commentatorFragment);

    if (commentator) {
        const defaultValues = zCommentatorInput.parse({
            ...commentator,
            shortDescription: commentator.shortDescription ?? "",
        });
        return <CommentatorProfileFormView defaultValues={defaultValues} />;
    } else {
        return (
            <CommentatorProfileFormView
                defaultValues={{ displayName: authUser?.displayName ?? "" }}
            />
        );
    }
}

function CommentatorProfileFormView({
    defaultValues,
}: {
    defaultValues: Partial<CommentatorInput>;
}) {
    const { refetch: refetchViewerInContext } = useViewerContext();
    const languageCode = useLanguageCodeWithDefault();

    const form = useForm<z.infer<typeof zCommentatorInput>>({
        defaultValues,
        mode: "onBlur",
        resolver: zodResolver(zCommentatorInput),
    });

    const [_res, upsertViewerCommentator] = useMutation(
        graphql(`
            mutation UpsertViewerCommentator($input: UpsertViewerCommentatorInput!) {
                upsertViewerCommentator(input: $input) {
                    id
                }
            }
        `)
    );

    const onSubmit: SubmitHandler<CommentatorInput> = async ({ shortDescription, ...formData }) => {
        const result = await upsertViewerCommentator({
            input: {
                ...formData,
                shortDescription: {
                    text: shortDescription,
                    languageCode,
                },
            },
        });

        if (result.error) {
            toast.error("Failed to update commentator profile", {
                description: result.error.message,
            });
            for (const error of result.error.graphQLErrors) {
                // @ts-expect-error typed as string from the API
                form.setError(error.name, { message: error.message });
            }
            return;
        }

        await refetchViewerInContext();

        toast.success("Commentator profile updated");
    };

    const selectedLanguage = useMemo(
        () => supportedLanguages.find(l => l.code === languageCode),
        [languageCode]
    );

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-8">
                <FormField
                    control={form.control}
                    name="displayName"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Display name</FormLabel>
                            <FormControl>
                                <Input placeholder="Name in app" {...field} />
                            </FormControl>
                            <FormDescription>
                                This is the name that will be displayed in the app.
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="shortDescription"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Short description (in {selectedLanguage?.name})</FormLabel>
                            <FormControl>
                                <Textarea
                                    placeholder={`Introduce yourself in ${selectedLanguage?.name}`}
                                    {...field}
                                />
                            </FormControl>
                            <FormDescription>
                                Short description (max 40 characters).
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="countryCode"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Country of origin</FormLabel>
                            <FormControl>
                                <SearchAndSelectInput
                                    value={
                                        field.value
                                            ? countryNamesByCodes[field.value as CountryCode]
                                            : null
                                    }
                                    onValueChange={name =>
                                        field.onChange(countryCodesByNames[name])
                                    }
                                    suggestions={countries.map(c => {
                                        return {
                                            label: `${getFlagEmoji(c.code)} ${c.name}`,
                                            value: c.name,
                                        };
                                    })}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="playerId"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Link FIDE player profile</FormLabel>
                            <FormControl>
                                <PlayerIdInput
                                    playerId={field.value}
                                    onPlayerIdChange={playerId =>
                                        form.setValue("playerId", playerId ?? null)
                                    }
                                />
                            </FormControl>
                            <FormDescription>
                                Link your commentator profile to a FIDE player profile.
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="imageId"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Image</FormLabel>
                            <FormControl>
                                <ImageUploadInput
                                    imageId={field.value ?? null}
                                    onImageId={imageId =>
                                        imageId
                                            ? form.setValue(field.name, imageId)
                                            : form.resetField(field.name)
                                    }
                                />
                            </FormControl>
                            <FormDescription>
                                This variant may be used when the player is doing well.
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <Button
                    type="submit"
                    disabled={!form.formState.isValid || form.formState.isSubmitting}
                >
                    Save
                </Button>
            </form>
        </Form>
    );
}
